/**
 * Chart / data-visualization color tokens.
 *
 * A different color job than the semantic UI tokens: charts need *categorical*
 * color (distinct hues = distinct series) and *sequential* color (shades of one
 * hue = drill-down/magnitude). The UI ramps in `primitives.ts` were tuned for
 * buttons/text/borders and fail as chart colors (lightness spread, low chroma),
 * so these are purpose-tuned steps designed against the brand. At design time the
 * palette was validated for CVD-simulated colorblind separation, a shared
 * lightness band, and chroma; the in-repo regression guard (chart.test.ts) pins
 * the adjacent-pair distance for NORMAL vision only — the CVD simulation is not
 * re-run in CI.
 *
 * The ADJACENT-pair separation is the validated guarantee; non-adjacent pairs
 * make no such promise (amber and citron sit close even to normal vision and
 * converge fully under deuteranopia). Charts must therefore never rely on color
 * alone — direct labels / position carry identity (WCAG 1.4.1), color
 * reinforces it.
 *
 * Shades are pre-computed and shipped static — this package stays
 * zero-runtime-dependency; nothing derives color in the browser.
 */
/**
 * The 12 categorical slots, in assignment order. Series take slots in FIXED
 * order — slot by entity, never cycled or re-ranked, and a 13th category folds
 * into "Other" rather than getting a new hue. `light`/`dark` are the fill for
 * the respective chart surface (the app is light-only today; dark ships
 * correct-in-waiting). The first slot leads with the brand-purple family on
 * purpose: TEO charts open purple, not generic-SaaS blue.
 */
export const chartCategorical = [
    { name: 'plum', light: '#9b499a', dark: '#924191' },
    { name: 'amber', light: '#e3a345', dark: '#bd7f13' },
    { name: 'teal', light: '#1c9988', dark: '#008a7b' },
    { name: 'brick', light: '#b74844', dark: '#ad3e3c' },
    { name: 'citron', light: '#c2b236', dark: '#9e9005' },
    { name: 'blue', light: '#4e7dc7', dark: '#4271ba' },
    { name: 'olive', light: '#81a23b', dark: '#709026' },
    { name: 'orchid', light: '#d3659a', dark: '#c1558a' },
    { name: 'sky', light: '#50bae7', dark: '#33a2ce' },
    { name: 'umber', light: '#a86137', dark: '#9d572d' },
    { name: 'slate', light: '#5067a9', dark: '#4361a1' },
    { name: 'lavender', light: '#b589d4', dark: '#ae7dc5' },
];
/**
 * Per-anchor sequential ramps for drill-down: click a category, its
 * subcategories render as these shades of the parent hue, so they read as
 * "children of" that slice. All 12 share one lightness spine (~0.93 → 0.46
 * OKLCH, anchor at index 3) so the drill-down brightness ladder is identical
 * regardless of which parent was clicked; `light`/`dark` variants share their
 * anchor step. Six steps is the discriminability ceiling — a deeper drill-down
 * groups its tail, it does not get more shades.
 *
 * NB: the index-3 anchor step is NOT the categorical fill for its slot (plum's
 * ramp[3] is `#c26ec0`, its slice fill `#9b499a`) — the ramps deliberately
 * re-normalize onto the shared spine so every drill-down reads identically.
 * Don't reach into a ramp expecting the parent slice's exact color.
 */
export const chartSequential = {
    plum: {
        light: ['#f8dff6', '#e9bbe6', '#d894d5', '#c26ec0', '#a24fa0', '#7b387a'],
        dark: ['#f2caf0', '#e5aae2', '#d68ad3', '#c26ec0', '#a658a4', '#884786'],
    },
    amber: {
        light: ['#f7e5cd', '#e8c69b', '#d7a662', '#c2841c', '#9d6801', '#764d02'],
        dark: ['#f1d4ae', '#e4b97f', '#d59e4f', '#c2841c', '#a56d02', '#865802'],
    },
    teal: {
        light: ['#d4efe9', '#a6d8ce', '#71c1b2', '#32a796', '#028778', '#016559'],
        dark: ['#b8e4db', '#8cd1c3', '#5fbcac', '#32a796', '#178d7e', '#107366'],
    },
    brick: {
        light: ['#fee0dc', '#fabab3', '#ef928b', '#dd6a64', '#ba4b47', '#8f3532'],
        dark: ['#fecbc6', '#faa8a1', '#ef8880', '#dd6a64', '#be544f', '#9c443f'],
    },
    citron: {
        light: ['#ede9cb', '#d6cf95', '#beb258', '#a49403', '#837603', '#615801'],
        dark: ['#e1dbaa', '#cdc477', '#b9ac40', '#a49403', '#897c01', '#6f6502'],
    },
    blue: {
        light: ['#dbe9ff', '#b3cef7', '#8ab1ed', '#6292de', '#4574bd', '#305691'],
        dark: ['#c3dbff', '#a0c3f8', '#7faaee', '#6292de', '#4d7ac0', '#3e639d'],
    },
    olive: {
        light: ['#e2edd2', '#c1d5a3', '#a0bb70', '#7fa039', '#628110', '#486102'],
        dark: ['#d0e1b5', '#b3cc8a', '#98b660', '#7fa039', '#698724', '#556e1b'],
    },
    orchid: {
        light: ['#ffddeb', '#f5b8d1', '#e890b7', '#d5679c', '#b3487e', '#89325e'],
        dark: ['#fec7de', '#f4a6c8', '#e885b2', '#d5679c', '#b75183', '#96416b'],
    },
    sky: {
        light: ['#d6ecf6', '#abd3e6', '#7cb9d5', '#4b9ec0', '#287fa0', '#155f7a'],
        dark: ['#bddff0', '#95cae2', '#6eb4d3', '#4b9ec0', '#3585a4', '#2a6c86'],
    },
    umber: {
        light: ['#f4e5db', '#e1c6b5', '#cea68d', '#b68666', '#976949', '#724d33'],
        dark: ['#ecd4c5', '#dcb9a2', '#cb9f82', '#b68666', '#9b6f51', '#7f5a41'],
    },
    slate: {
        light: ['#e5e8f1', '#c6ccdc', '#a7afc6', '#8891af', '#6b7390', '#4f566c'],
        dark: ['#d4d9e7', '#bac0d5', '#a0a8c3', '#8891af', '#717994', '#5c6279'],
    },
    lavender: {
        light: ['#ede3f6', '#d5c4e6', '#bda3d4', '#a282bf', '#84659f', '#634a79'],
        dark: ['#e1d2ef', '#ccb6e2', '#b89bd2', '#a282bf', '#896ba4', '#705786'],
    },
};
